import { ComponentProps, useEffect, useState } from 'react';

import * as icons from '@creditornot/alchemy-react/icons';
import { Input } from '@creditornot/cb-input';

import { useFormatMessage } from '../../../hooks/useFormatMessage';

type InputProps = ComponentProps<typeof Input>;

export type SearchInputProps = Omit<InputProps, 'onChange'> & {
  debounceDelay?: number;
  onChange?: (value: string) => void;
  value?: string;
  onClear?: () => void;
};

export function SearchInput({
  debounceDelay = 225,
  onChange,
  placeholder,
  role = 'searchbox',
  value,
  onClear,
  ...props
}: SearchInputProps) {
  const f = useFormatMessage();
  const [newValue, setNewValue] = useState<undefined | string>(value);
  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    setFirstRender(false);
  }, []);

  useEffect(() => {
    if (firstRender || newValue === undefined || !onChange) {
      return;
    }

    const handler = setTimeout(() => {
      onChange(newValue);
    }, debounceDelay);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceDelay, newValue]);

  useEffect(() => {
    if (value !== newValue) {
      setNewValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      {...props}
      icon={icons.magnifyingGlass}
      onChange={(event) => setNewValue(event.target.value)}
      onClearClick={() => {
        setNewValue('');
        if (onClear) {
          onClear();
        }
      }}
      placeholder={placeholder ?? f('common.search')}
      role={role}
      type="text"
      value={newValue}
    />
  );
}
